import {
    ArrowBack,
    Facebook,
    Instagram,
    Search,
    Twitter,
    YouTube,
} from '@mui/icons-material';
import {
    AppBar,
    Box,
    Button,
    Fab,
    IconButton,
    InputBase,
    Paper,
    Toolbar,
    Typography,
    useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetListDataWithGetMutation } from '../../api/apiSlice';
import logo from '../../assets/favicon.png';
import ExternalScreen from '../../components/fragments/ExternalScreen';
import FCMClient from '../../components/fragments/FCMClient';
import GuestFeedItem from '../../components/fragments/GuestFeedItem';
import ListLoading from '../../components/fragments/ListLoading';
import useAuth from '../../hooks/useAuth';
import { useSelector } from '../../store';

export default function GuestFeedScreen() {
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [feedList, updateFeedList] = useState([]);

    const { isChurchMember } = useAuth();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const state = useSelector((st) => st);
    const [getGuestPosts, { isLoading }] = useGetListDataWithGetMutation();

    const { churches } = state.list2;
    // const { feedList } = state.feed;

    const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } =
            document.documentElement;

        if (scrollTop + clientHeight >= scrollHeight - 500) {
            dispatch(setCurrentPage(currentPage + 1));
        }
    };

    useEffect(() => {
        (async () => {
            const { data: postsData } = await getGuestPosts({
                list: `guest?page=${currentPage}`,
            });
            const nextPage = postsData?.data || [];
            let updatedList = [];

            if (feedList && feedList[0]?.data?.id != nextPage[0]?.data?.id)
                updatedList = [...feedList, ...nextPage];
            else updatedList = nextPage;

            updateFeedList(updatedList);
        })();

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const filteredFeed = feedList?.filter((post) =>
        JSON.stringify(post)?.toLowerCase().includes(search?.toLowerCase())
    );

    return (
        <ExternalScreen
            title="myKanisa"
            loading={isLoading}
            authPass
            disableAppBar
            disablePadding
            disableSpacing
            tab
        >
            <FCMClient />
            <GuestAppBar onSearch={(text) => setSearch(text)} />
            <Box sx={{ position: 'relative', top: 56 }} />
            <Box sx={{ p: 2, pt: 0 }}>
                <Box sx={{ width: '100%', height: 72 }} />
                <Typography className="mb-1" variant="h5">
                    {isChurchMember
                        ? 'Explore your Church'
                        : 'Welcome to myKanisa App'}
                </Typography>
                <Typography className="fw-light" variant="body">
                    {isChurchMember
                        ? 'Get back to your church account and explore your church activities'
                        : 'Join a church and get a personalised connection with your church'}
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() =>
                            navigate(isChurchMember ? '/' : '/register')
                        }
                    >
                        {isChurchMember ? 'Go to Church' : 'Join a Church'}
                    </Button>
                </Box>
            </Box>
            {filteredFeed?.map(({ data, type }) => (
                <GuestFeedItem
                    key={data?.id + type}
                    feed={data}
                    type={`${type}s`}
                    churches={churches}
                />
            ))}
            {isLoading && <ListLoading />}
        </ExternalScreen>
    );
}

export const GuestAppBar = ({ onSearch, tab }) => {
    const [searchOpen, setSearchOpen] = useState(false);
    const [socialOpen, setSocialOpen] = useState(false);

    const navigate = useNavigate();
    const classes = useStyles();
    const state = useSelector((st) => st);

    const { user } = state.auth;
    const isLoggedIn = user ? Object.keys(user).length > 0 : false;

    return (
        <div className={classes.root}>
            <AppBar className={classes.appbar} position="fixed" elevation={4}>
                <Toolbar className={classes.toolbar}>
                    {!tab &&
                        !!isLoggedIn &&
                        !searchOpen &&
                        !!user?.church?.id && (
                            <IconButton
                                edge="start"
                                className={classes.menuButton}
                                aria-label="menu"
                                onClick={() => navigate('/feed')}
                            >
                                <ArrowBack />
                            </IconButton>
                        )}
                    {searchOpen && (
                        <>
                            <IconButton
                                edge="start"
                                className={classes.menuButton}
                                aria-label="menu"
                                onClick={() => setSearchOpen(false)}
                            >
                                <ArrowBack />
                            </IconButton>
                            <InputBase
                                placeholder="Search…"
                                classes={{
                                    root: classes.inputRoot,
                                    input: classes.inputInput,
                                }}
                                inputProps={{ 'aria-label': 'search' }}
                                onChange={(e) => onSearch(e.target.value)}
                            />
                        </>
                    )}
                    {!searchOpen && (
                        <Typography
                            variant="h5"
                            noWrap={true}
                            className={classes.title}
                        >
                            myKanisa
                        </Typography>
                    )}
                    {!searchOpen && (
                        <div>
                            <IconButton
                                color="primary"
                                onClick={() => setSearchOpen(true)}
                            >
                                <Search />
                            </IconButton>
                            <IconButton onClick={() => setSocialOpen(true)}>
                                <img
                                    style={{ width: 24, height: 24 }}
                                    src={logo}
                                    alt="logo"
                                />
                            </IconButton>
                        </div>
                    )}
                </Toolbar>
            </AppBar>
            {socialOpen && (
                <GuestSocialBar onSocialClose={() => setSocialOpen(false)} />
            )}
        </div>
    );
};

export const GuestSocialBar = ({ onSocialClose }) => {
    const theme = useTheme();

    return (
        <Paper
            elevation={8}
            className="d-flex flex-column align-items-end  p-1"
            style={{
                position: 'fixed',
                right: 10,
                top: 8,
                zIndex: 1200,
                borderRadius: theme.shape.borderRadius,
            }}
        >
            <Fab
                onClick={() => onSocialClose()}
                className="mb-2"
                size="small"
                aria-label="add"
            >
                <img style={{ width: 24, height: 24 }} src={logo} alt="logo" />
            </Fab>
            {actionButtons?.map(({ link, icon, bg }, index) => (
                <div key={link} style={{ transition: 'opacity 0.5s linear' }}>
                    <Fab
                        onClick={() => window.open(link, '_blank')}
                        className={index !== actionButtons.length - 1 && 'mb-2'}
                        size="small"
                        color="primary"
                        aria-label="add"
                        style={{ backgroundColor: bg }}
                    >
                        {icon}
                    </Fab>
                </div>
            ))}
        </Paper>
    );
};

const useStyles = makeStyles((theme) => ({
    menuButton: {
        marginRight: theme.spacing(2),
        border: 'none',
    },
    title: {
        flexGrow: 1,
        fontWeight: 'bold',
        color:
            theme.palette.mode === 'light'
                ? theme.palette.primary.main
                : '#fff',
    },
    toolbar: {
        background: theme.palette.background.default,
        boxShadow: 'none',
    },
    appbar: {
        margin: 'auto',
        backgroundColor: theme.palette.mode === 'light' && '#fff',
    },
    iconButton: {
        backgroundColor: theme.palette.primary.main,
    },
}));

const actionButtons = [
    {
        link: 'https://facebook.com/morfieltd',
        label: 'Facebook',
        icon: <Facebook />,
        bg: '#3b5998',
    },
    {
        link: 'https://instagram.com/morfieltd',
        label: 'Instagram',
        icon: <Instagram />,
        bg: '#e1306c',
    },
    {
        link: 'https://twitter.com/LtdMorfie',
        label: 'Twitter',
        icon: <Twitter />,
        bg: '#1da1f2',
    },
    {
        link: 'https://www.youtube.com/channel/UChvOt6TFpvyMibeAtsrT3oA',
        label: 'YouTube',
        icon: <YouTube />,
        bg: '#ff0000',
    },
];
